import React from 'react';
import { Box, Button, Slide, Stack, Tooltip, Typography } from '@mui/material';
import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import CloseIcon from '@mui/icons-material/Close';

interface ContextualActionBarProps {
  count: number;
  exerciseDisabled?: boolean;
  exerciseDisabledReason?: string;
  /**
   * When provided, surfaces a "→ Нові" action. Used on the attention tab to
   * let the user manually mark words as real English (so they re-sort into
   * "Нові" with a pending-translation placeholder).
   */
  onPromoteToNew?: () => void;
  onAddToVocabulary: () => void;
  onStartExercise: () => void;
  onIgnore: () => void;
  onClear: () => void;
}

// Floating bottom-of-card action bar. Only renders when something is selected.
// Replaces the static toolbar that previously hung at minHeight=50 even when
// no rows were selected.
const ContextualActionBar: React.FC<ContextualActionBarProps> = ({
  count,
  exerciseDisabled,
  exerciseDisabledReason,
  onAddToVocabulary,
  onStartExercise,
  onPromoteToNew,
  onIgnore,
  onClear,
}) => (
  <Slide in={count > 0} direction={'up'} mountOnEnter unmountOnExit>
    <Box
      sx={(theme) => ({
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 3,
        bottom: 16,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 8px 24px rgba(0,0,0,0.4)'
            : '0 8px 24px rgba(59,44,147,0.18)',
        left: { xs: 16, md: '50%' },
        position: 'sticky',
        px: 2,
        py: 1.5,
        right: { xs: 16, md: 'auto' },
        transform: { md: 'translateX(-50%)' },
        width: { xs: 'auto', md: 'fit-content' },
        zIndex: 10,
      })}
    >
      <Stack
        direction={'row'}
        alignItems={'center'}
        spacing={1.5}
        flexWrap={'wrap'}
      >
        <Stack direction={'row'} alignItems={'center'} spacing={1}>
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'primary.purple',
              borderRadius: '50%',
              color: 'primary.main',
              display: 'flex',
              fontSize: 13,
              fontWeight: 800,
              height: 28,
              justifyContent: 'center',
              minWidth: 28,
              px: 0.75,
            }}
          >
            {count}
          </Box>
          <Typography variant={'body2'} fontWeight={600}>
            {'Обрано'}
          </Typography>
        </Stack>

        <Stack direction={'row'} spacing={1}>
          <Button
            startIcon={<LibraryAddOutlinedIcon />}
            onClick={onAddToVocabulary}
            size={'small'}
          >
            {'У словник'}
          </Button>
          {onPromoteToNew && (
            <Button
              startIcon={<NewReleasesOutlinedIcon />}
              onClick={onPromoteToNew}
              size={'small'}
            >
              {'→ Нові'}
            </Button>
          )}
          <Button
            startIcon={<BlockOutlinedIcon />}
            onClick={onIgnore}
            size={'small'}
            variant={'outlined'}
          >
            {'У ігнор'}
          </Button>
          {exerciseDisabled ? (
            <Tooltip title={exerciseDisabledReason || ''}>
              <span>
                <Button
                  startIcon={<FitnessCenterOutlinedIcon />}
                  disabled
                  size={'small'}
                >
                  {'Сесія'}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              startIcon={<FitnessCenterOutlinedIcon />}
              onClick={onStartExercise}
              size={'small'}
            >
              {'Сесія'}
            </Button>
          )}
          <Button
            startIcon={<CloseIcon />}
            onClick={onClear}
            size={'small'}
            variant={'text'}
          >
            {'Зняти'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  </Slide>
);

export default ContextualActionBar;
